import * as React from "react";
import {useEffect, useState} from "react";
import {Col, Row} from "react-bootstrap";
import {currentLocation, Routes} from "../utils/router";
import {Customization} from "../types/sessionsstate";
import {loadFromLocalStorage, tryCall} from "../utils/commons.ts";
import {
    BrowserCompatibility,
    checkTreeCopyPaste,
    compatibilityTexts,
    copyPasteRequirements,
    getBrowserVersion,
    minimumRequirements
} from "../utils/BrowserCompatibility";
import {
    confirmBox, delay,
    IMAGES,
    KButton,
    KBUTTON_SIZE,
    KBUTTON_TYPE,
    KBUTTON_VARIANT,
    KCheck,
    KContainer,
    KFileUpload,
    KForm,
    KInput,
    KModal,
    KModalBackdrop,
    KModalVariant,
    KSpace,
    Translate
} from "@kopjra/uikit";
import {KColorPicker} from "@kopjra/uikit/dist/src/lib/KColorPicker";
import {Feature, hasFeature} from "../utils/features";
import tinycolor from "tinycolor2";
import {useAuth0} from "@auth0/auth0-react";
import {useTranslation} from "react-i18next";
import {useCreateSessionMutation} from "../api/session.ts";
import {DangerHtml} from "./DangerHtml.tsx";
import {ConnectivityCheckResult, verifyConnectivity} from "../utils/connectivity.ts";

function renderCheckTree(checkTree: BrowserCompatibility): JSX.Element[] {
    return Object.keys(checkTree).map((env, index) => (
        <div key={index}>
            {Object.keys(checkTree[env as (keyof BrowserCompatibility)]).map((browser, innerIndex) => (
                <Row key={innerIndex}>
                    <Col className="right">
                        <a href={compatibilityTexts[browser].url} target="_blank" rel="noopener noreferrer">
                            <span className="browserName">{compatibilityTexts[browser].name}</span>
                            <span className="browserVersion">{compatibilityTexts[browser].version}</span>
                        </a>
                    </Col>
                </Row>
            ))}
        </div>
    ));
}

function renderBrowserVersion(): React.JSX.Element {
    const browserVersion = getBrowserVersion();
    const pointPos = browserVersion.browser.version ? browserVersion.browser.version.indexOf(".") : -1;
    const version = browserVersion.browser.version ? browserVersion.browser.version.substring(0, (pointPos !== -1) ? pointPos : undefined) : "";
    return (
        <Row>
            <Col className="right">
                <span className="browserName">{browserVersion.browser.name}</span>
                <span className="browserVersion">{version}</span>
            </Col>
        </Row>
    );
}

enum ValidationState {
    NONE,
    GOOD,
    MEH,
    BAD,
}

function getValidationState(): ValidationState {
    const minRequirements = minimumRequirements();
    const compatible = minRequirements && copyPasteRequirements();
    return (compatible) ? ValidationState.GOOD : (minRequirements ? ValidationState.MEH : ValidationState.BAD);
}

export const CreateSession: React.FC = () => {
    const {user} = useAuth0();
    const location = currentLocation;

    const [sessionName, setSessionName] = useState<string>("");
    const [fullProxy, setFullProxy] = useState<boolean>(false);
    const [customization, setCustomization] = useState<Customization>(loadFromLocalStorage("customization") || {});

    function hasCustomization(): boolean {
        return !!(customization && (customization.subtitle || customization.logo || customization.color));
    }

    const [customOpened, setCustomOpened] = useState<boolean>(hasCustomization());
    const [validationState, setValidationState] = useState<ValidationState>(ValidationState.NONE);
    const [connectivityCheck, setConnectivityCheck] = useState<ConnectivityCheckResult | undefined>();

    const [submitting, setSubmitting] = useState<boolean>(false);

    const [triggerCreateSession] = useCreateSessionMutation();

    useEffect(() => {
        document.body.classList.toggle("dark", true);
        return () => {
            document.body.classList.toggle("dark", false);
        };
    }, [location.pathname]);

    useEffect( () => {
        if (validationState === ValidationState.NONE) {
            setValidationState(getValidationState());
        }
    }, [setValidationState]);

    useEffect(() => {
        (async () => {
            const ccResult = await verifyConnectivity();
            setConnectivityCheck(ccResult);
        })().catch(console.log);
    }, [setConnectivityCheck]);

    async function handleSubmit() {
        const vs = getValidationState();
        let ccResult: ConnectivityCheckResult | undefined;
        try {
            ccResult = await verifyConnectivity();
        } catch {
            ccResult = {status: "KO", reason: t("new.wssFailedToConnect")};
        }
        if ((vs !== ValidationState.BAD && vs !== ValidationState.MEH && ccResult?.status === "OK") || await confirmBox({
            noText: t("new.no"),
            yesText: t("new.yes"),
            dark: true,
            message: `${(ccResult?.status !== "OK") ? ccResult.reason : (vs === ValidationState.BAD) ? t("new.notCompatible") : t("new.minBrowser")}<br/><br/>${t("new.continue")}`
        })) {
            if (!await tryCall(triggerCreateSession({customization, sessionInput: {name: sessionName, fullProxy}}).unwrap(), {
                error: {
                    402: t("error.paymentRequired"),
                    default: t("error.sessionCreation"),
                }
            }, async (createdSession) => {
                if (createdSession) {
                    Routes.goNavigate(`${createdSession.id}`);
                    await delay(1000); // This is to ensure the navigation completion before the submit button is released from waiting state
                }
            })) {
                Routes.goSessions();
            }
        }
    }
    
    const {t} = useTranslation();

    return (
        <KContainer>
            <>
                <KSpace/>
                <Row className="createLogo">
                    <Col className="text-center">
                        <img
                            alt="logo_big"
                            src={IMAGES["web-forensics"].still.large.dark}
                            height={110}
                        />
                    </Col>
                </Row>
                <Row className="createTitle">
                    <Col className="text-center">
                        <h2><Translate i18nKey={"new.title"}/></h2>
                    </Col>
                </Row>
                <KModal show={connectivityCheck?.status === "KO" || validationState === ValidationState.MEH || validationState === ValidationState.BAD}
                        header={<Translate i18nKey="new.attention"/>}
                        onHide={() => {setValidationState(ValidationState.NONE); setConnectivityCheck(undefined)}}
                        buttonText={<Translate i18nKey="new.close"/>}
                        backdrop={KModalBackdrop.static} closeButton={true} dark={true} variant={(validationState === ValidationState.MEH) ? KModalVariant.warning : KModalVariant.danger}>
                    {connectivityCheck?.status === "KO" ? (
                        <Row style={{marginTop: 10}}>
                            <Col className={"compatible-no"}>
                                <DangerHtml html={connectivityCheck.reason || ""}/>
                            </Col>
                        </Row>
                    ) : null}
                    {validationState === ValidationState.MEH || validationState === ValidationState.BAD ? (
                        <>
                            <Row style={{marginTop: 10}}>
                                <Col className={(validationState === ValidationState.MEH ? "compatible-min" : "compatible-no")}>
                                    {(validationState === ValidationState.MEH) ? <Translate i18nKey="new.minBrowser"/> : <Translate i18nKey="new.notCompatible"/>}
                                </Col>
                            </Row>
                            <Row style={{marginTop: -10}}>
                                <Col className="compatible-subtitle">
                                    <Translate i18nKey="new.subtitle"/>
                                </Col>
                            </Row>
                            <div className="compatibility">
                                <Row style={{marginTop: 20}}>
                                    <Col xs={{span: 4}} className="envName"><Translate i18nKey="new.yourBrowser"/></Col>
                                    <Col xs={4}>{renderBrowserVersion()}</Col>
                                </Row>
                                <Row style={{marginTop: 20}}>
                                    <Col xs={{span: 4}} className="envName"><Translate i18nKey="new.recommended"/></Col>
                                    <Col xs={4}>{renderCheckTree(checkTreeCopyPaste)}</Col>
                                </Row>
                            </div>
                        </>
                    ) : null}
                </KModal>
                <KForm onSubmit={handleSubmit} onSubmitting={(submitting) => setSubmitting(submitting)}>
                    <KSpace spaces={3}/>
                    <Row>
                        <Col md={4}>
                            <KInput id="sessionName"
                                    label={<Translate i18nKey={"new.namelabel"}/>}
                                    value={sessionName}
                                    onChange={({currentTarget}) => setSessionName(currentTarget.value)}
                                    errorMessage={new Map()
                                        .set("required", <Translate i18nKey="new.namerequired"/>)
                                        .set("pattern", <DangerHtml html={t("new.patternError")}/>)
                                    }
                                    dark={true}
                                    placeholder={t("new.defaultName")}
                                    pattern={"^[A-Za-zÀ-ÿ0-9()_+,;. -]*$"}
                                    required={true}
                            />
                        </Col>
                    </Row>
                    {user?.assigned_roles.includes("wf:proxy") ? (
                        <Row>
                            <Col md={4} className={"darkmode"}>
                                <KCheck
                                    id="fullProxy" checked={fullProxy} label={<Translate i18nKey={"new.fullProxy"}/>} dark={true}
                                    onChange={({currentTarget}) => setFullProxy(currentTarget.checked)}
                                />
                            </Col>
                        </Row>
                    ) : null}
                    {!hasFeature(Feature.GFL) ? (
                        <>
                            <KSpace spaces={2}/>
                            <Row>
                                <Col md={4}>
                                    <h2 className="customOpener" onClick={() => setCustomOpened(!customOpened)}><Translate i18nKey={"new.customization"}/>
                                        {hasCustomization() ? <i className={"fal fa-asterisk"} style={{fontSize: "0.7rem", position: "relative", top: -5}}/> : null} <i className={`fal ${customOpened ? "fa-caret-up" : "fa-caret-down"}`}/></h2>
                                </Col>
                            </Row>
                            <Row className="customizationContainer" style={{display: customOpened ? "inherit" : "none"}}>
                                <Col md={{span: 4, offset: 4}}>
                                    <KButton text={<Translate i18nKey={"new.reset"}/>} variant={KBUTTON_VARIANT.link} onClick={() => {
                                        setCustomization({});
                                    }}/>
                                    <Row>
                                        <Col>
                                            <KInput id="subtitle"
                                                    label={<Translate i18nKey={"new.subtitlelabel"}/>}
                                                    value={customization.subtitle || ""}
                                                    dark={true}
                                                    hint={<Translate i18nKey={"new.subtitlehint"}/>}
                                                    onChange={({currentTarget}) => {
                                                        setCustomization({
                                                            ...customization,
                                                            subtitle: currentTarget.value
                                                        });
                                                    }}
                                                    validate={(value: string) => value.length <= 100}
                                                    errorMessage={<Translate i18nKey={"new.subtitleError"}/>}
                                            />
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col>
                                            <KFileUpload id={"logo"} label={<Translate i18nKey={"new.logolabel"}/>}
                                                         dark={true}
                                                         defaultPreloadedImage={customization.logo}
                                                         showUploadedFilename={false}
                                                         preloadImage={true}
                                                         hint={<Translate i18nKey={"new.logohint"}/>}
                                                         onChange={(content, mime) => {
                                                             setCustomization({
                                                                 ...customization,
                                                                 logo: {content: content as string, mime: mime as string},
                                                                 logoId: undefined,
                                                             });
                                                         }}
                                                         accept={["image/png", "image/jpeg"]}
                                                         maxSizeInMb={1}
                                                         maxSizeErrorMessage={<Translate i18nKey={"new.logoMaxSize"}/>}
                                            />
                                        </Col>
                                    </Row>
                                    <KSpace/>
                                    <Row>
                                        <Col>
                                            <KColorPicker id={"color"}
                                                          label={<Translate i18nKey={"new.colorlabel"}/>}
                                                          dark={true}
                                                          hint={<Translate i18nKey={"new.colorhint"}/>}
                                                          value={customization.color || "#419ED8"}
                                                          validate={(value) => tinycolor(value).getLuminance() < 0.82}
                                                          errorMessage={<Translate i18nKey={"new.colorError"}/>}
                                                          onChange={(color) => {
                                                              setCustomization({
                                                                  ...customization,
                                                                  color,
                                                              });
                                                          }}
                                            />
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>
                        </>
                    ) : null}
                    <KSpace spaces={4}/>
                    <Row>
                        <Col xs={{span: 4}}>
                            <KButton variant={KBUTTON_VARIANT.primary}
                                     type={KBUTTON_TYPE.submit}
                                     waiting={submitting}
                                     text={<><i className="fal fa-lg fa-play"/>&nbsp;&nbsp;<Translate i18nKey="new.startButton"/></>}
                                     size={KBUTTON_SIZE.lg}
                                     fill={true}
                            />
                        </Col>
                    </Row>
                </KForm>
                <KSpace spaces={10}/>
            </>
        </KContainer>
    );
};
